import { parseCookies } from '@/lib/cookies';
import { FEATURES } from './utils/permissions';

// Helper to get runtime config (server: process.env, client: window.__ENV__)
const getEnv = key => {
  if (typeof window !== 'undefined' && window.__ENV__) {
    return window.__ENV__[key];
  }
  return process.env[key];
};

let RAILS_ENV = getEnv('NEXT_PUBLIC_RAILS_ENV');
const IS_DEV = ['development'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_STAGE = ['stage'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_DEMO = ['demo'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_DEV_STAGE_DEMO = ['development', 'stage', 'demo'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_DEV_STAGE = ['development', 'stage'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_DEV_DEMO = ['development', 'demo'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));
const IS_STAGE_DEMO = ['stage', 'demo'].includes(getEnv('NEXT_PUBLIC_APP_ENV'));

// Cookie-based RAILS_ENV override for dev/stage/demo
if (IS_DEV_STAGE_DEMO) {
  const cookieDomain = parseCookies()?.domain;
  if (cookieDomain) {
    RAILS_ENV = cookieDomain;
  }
}

const MYMADCRM = RAILS_ENV === 'production';
const AEGLEADS = RAILS_ENV === 'production_aegleads';
const MYASBAGENT = RAILS_ENV === 'production_myasbagent';
const MYSIBAGENT = RAILS_ENV === 'production_mysibagent';
const ASBMIDWEST = RAILS_ENV === 'production_asbmidwest';
const LEGACYCRM = RAILS_ENV === 'production_legacycrm';
const MYCSSAGENT = RAILS_ENV === 'production_mycssagent';
const BROKERAGEASB = RAILS_ENV === 'production_brokerageasb';
const MYARCCRM = RAILS_ENV === 'production_myarccrm';
const CFGSNE = RAILS_ENV === 'production_cfgsne';
const ASBCENTRAL = RAILS_ENV === 'production_asbcentral';
const IPAAGENT = RAILS_ENV === 'production_ipaagent';
const MYASBAGENT_SYSTEM =
  MYASBAGENT || MYSIBAGENT || ASBMIDWEST || LEGACYCRM || BROKERAGEASB || MYARCCRM || ASBCENTRAL || IPAAGENT;

const ASBAGENTMN = RAILS_ENV === 'production_asbagentmn';
const ASBLEGACY = RAILS_ENV === 'production_asblegacy';
const MYASBFINANCIAL = RAILS_ENV === 'production_myasbfinancial';
const HRGAGENT = RAILS_ENV === 'production_hrgagent';
const MYAIMSAGENT = RAILS_ENV === 'production_myaimsagent';
const CAREINADVANCE = RAILS_ENV === 'production_careinadvance';
const CLIENTFLOW360 = RAILS_ENV === 'production_clientflow360';
const AGENTFLOW360 = RAILS_ENV === 'production_agentflow360';

const ASB_SYSTEM = MYASBAGENT_SYSTEM || ASBAGENTMN || ASBLEGACY || MYASBFINANCIAL || HRGAGENT || MYAIMSAGENT || CFGSNE;

export const config = (user = null) => {
  const AF360_BU11 =
    (AGENTFLOW360 && ['BU11'].includes(user?.business_unit?.number)) ||
    (IS_DEV && ['BU12_test'].includes(user?.business_unit?.number));

  const AF360_BU13 =
    (AGENTFLOW360 && ['BU13'].includes(user?.business_unit?.number)) ||
    (IS_DEV && ['BU12_test'].includes(user?.business_unit?.number));

  return {
    RAILS_ENV: RAILS_ENV,
    IS_DEV: IS_DEV,
    IS_STAGE: IS_STAGE,
    IS_DEMO: IS_DEMO,
    IS_DEV_STAGE_DEMO: IS_DEV_STAGE_DEMO,
    IS_DEV_STAGE: IS_DEV_STAGE,
    IS_DEV_DEMO: IS_DEV_DEMO,
    IS_STAGE_DEMO: IS_STAGE_DEMO,
    MYMADCRM: MYMADCRM,
    AEGLEADS: AEGLEADS,
    MYASBAGENT_SYSTEM: MYASBAGENT_SYSTEM,
    MYASBAGENT: MYASBAGENT,
    MYSIBAGENT: MYSIBAGENT,
    ASBMIDWEST: ASBMIDWEST,
    LEGACYCRM: LEGACYCRM,
    MYCSSAGENT: MYCSSAGENT,
    ASBAGENTMN: ASBAGENTMN,
    ASBLEGACY: ASBLEGACY,
    MYASBFINANCIAL: MYASBFINANCIAL,
    HRGAGENT: HRGAGENT,
    MYAIMSAGENT: MYAIMSAGENT,
    CAREINADVANCE: CAREINADVANCE,
    ASB_SYSTEM: ASB_SYSTEM,
    MYARCCRM: MYARCCRM,
    CFGSNE: CFGSNE,
    ASBCENTRAL: ASBCENTRAL,
    IPAAGENT: IPAAGENT,
    BROKERAGEASB: BROKERAGEASB,
    CLIENTFLOW360: CLIENTFLOW360,
    AGENTFLOW360: AGENTFLOW360,

    POLICY_SUBMISSION_AVAILABLE: (ASB_SYSTEM && !BROKERAGEASB) || MYMADCRM,
    POLICY_FILES_ENABLED: MYASBAGENT && ['C139', 'C148'].includes(user?.company?.number),
    CONTENT_CENTER: MYMADCRM,
    MEDICARE_CENTER: !IS_DEV && ASB_SYSTEM,
    COMRA_LINK: !user?.basic && ASB_SYSTEM && !BROKERAGEASB,
    RETIREFLO_LINK: !user?.basic && HRGAGENT,
    SHARED_CALENDAR_CONTACT_EDIT: AGENTFLOW360,
    USER_TITLE: !user?.basic && AGENTFLOW360,
    CONTACT_OFFICE_LOCATION: !user?.basic && AGENTFLOW360,
    CONTACT_OFFICE_ADDRESS: !user?.basic && AGENTFLOW360,
    LEGACY_ID: ASB_SYSTEM,
    GLIC_RX: !user?.basic && ASB_SYSTEM && !BROKERAGEASB,
    // REQUEST-81: ClientWave360 collects NPN on My Info and the admin edit-user form.
    NPN_NUMBER: ASB_SYSTEM || AEGLEADS || CLIENTFLOW360,
    PLANS_OFFERED_CARRIERS: !user?.basic && !CAREINADVANCE && !AGENTFLOW360,
    WRITING_VIEWING_AGENT_NUMBER: AEGLEADS || CAREINADVANCE,
    BUSINESS_UNIT: AEGLEADS || CAREINADVANCE || AGENTFLOW360 || CLIENTFLOW360,
    AF360_BU: AGENTFLOW360 && user?.bu_admin,
    CAMPAIGN_PAGES: AGENTFLOW360 || MYCSSAGENT || MYMADCRM,
    CAMPAIGN_PAGE_BILLING: MYMADCRM,
    CF360_IMPERSONATION: CLIENTFLOW360 && user?.permissions?.[FEATURES.IMPERSONATION]?.active === true,
    // REQUEST-75: custom domains for Marketing plans + Agency owners (permission is plan-derived)
    CF360_CUSTOM_DOMAIN: CLIENTFLOW360 && user?.permissions?.[FEATURES.MANAGE_COMPANY_DOMAIN]?.active === true,
    AF360_MANAGER: AGENTFLOW360 && user?.manager,
    TRANSFER_LOG: MYMADCRM || AEGLEADS || CAREINADVANCE || ASB_SYSTEM,
    // REQUEST-79: the ASB clause is unchanged; the CF360 clause requires a connected
    // agency integration and an agent the owner has not opted out.
    FIRELIGHT_SUBMISSION:
      (!user?.basic && ASB_SYSTEM && !MYARCCRM && !BROKERAGEASB) ||
      (CLIENTFLOW360 && user?.fire_light_enabled === true),
    TRANSFER_BY_COUNTY: HRGAGENT || ASBLEGACY,
    DISABLED_EXTERNAL_ACCOUNTS: MYMADCRM || user?.basic,
    CONTACT_ADDITIONAL_STATUSES_SOURCES: ASB_SYSTEM,
    DISABLED_ACTIVITY_TRACKING:
      user?.basic ||
      MYASBFINANCIAL ||
      BROKERAGEASB ||
      AGENTFLOW360 ||
      ASBCENTRAL ||
      CLIENTFLOW360 ||
      (MYASBAGENT && ['C32'].includes(user?.company?.number)),
    REMOVED_COPYRIGHT_LINE: ASB_SYSTEM,
    C1_NUMBER: AEGLEADS,
    VERIFIED_PHONE: !user?.basic,
    HELP_BUTTON: false,
    REMOVED_COMPANY_NAME_WEBPAGE: ASB_SYSTEM || AEGLEADS,
    COMPANIES_DESCRIPTION_NOT_REQUIRED: ASB_SYSTEM,
    CALL_GUIDE: user?.c_1,
    MINI_CALENDAR: (AGENTFLOW360 || CLIENTFLOW360 || ASB_SYSTEM || user?.aris) && !user?.basic,
    DEFAULT_VISIBLE_MINI_CALENDAR: AGENTFLOW360 || CLIENTFLOW360,
    DASHBOARD_MINI_CALENDAR: ASBMIDWEST,
    GOOGLE_MAP: ASB_SYSTEM || CLIENTFLOW360,
    FOLLOW_UPS: (ASB_SYSTEM || MYCSSAGENT || MYMADCRM || AGENTFLOW360 || user?.aris) && !user?.basic,
    REFERRED_BY:
      MYASBAGENT ||
      ASBCENTRAL ||
      IPAAGENT ||
      MYSIBAGENT ||
      ASBMIDWEST ||
      LEGACYCRM ||
      MYCSSAGENT ||
      AEGLEADS ||
      MYAIMSAGENT ||
      HRGAGENT ||
      ASBAGENTMN ||
      CFGSNE ||
      MYASBFINANCIAL ||
      AGENTFLOW360 ||
      MYMADCRM,
    COMPANY_FIELD:
      MYASBAGENT ||
      ASBCENTRAL ||
      IPAAGENT ||
      MYSIBAGENT ||
      ASBMIDWEST ||
      LEGACYCRM ||
      MYCSSAGENT ||
      ASBAGENTMN ||
      MYAIMSAGENT ||
      CFGSNE ||
      MYMADCRM ||
      MYASBFINANCIAL ||
      AGENTFLOW360 ||
      HRGAGENT,
    PLAN: AEGLEADS,
    AFFINITY: ASB_SYSTEM,
    CREDIT_UNION: !user?.basic && (MYASBAGENT || ASBCENTRAL),
    PRODUCTS_SOLD_ITS_TRUST: ASBLEGACY || (MYASBAGENT && ['C150'].includes(user?.company?.number)),
    LEAD_CAMPAIGN_ALLFORCE: (MYASBAGENT && ['C34', 'C53', 'C88', 'C93'].includes(user?.company?.number)) || ASBMIDWEST,
    FORCE_VERIFY_EMAIL: CFGSNE || ASBLEGACY,
    REMOVE_VERIFIED_PHONE: ASBLEGACY,
    DASHBOARD_AFFINITY_LEADS:
      MYASBAGENT ||
      ASBCENTRAL ||
      IPAAGENT ||
      MYSIBAGENT ||
      ASBLEGACY ||
      CFGSNE ||
      ASBAGENTMN ||
      HRGAGENT ||
      ASBMIDWEST ||
      MYAIMSAGENT,
    MULTI_ACCOUNT_ACCESS: ASB_SYSTEM || AEGLEADS || AGENTFLOW360 || CLIENTFLOW360,
    RM: CAREINADVANCE,
    REPORTS: !BROKERAGEASB && !user?.basic,
    EMAIL_LOG: !BROKERAGEASB,
    PRODUCT_INFO: !CAREINADVANCE && !AGENTFLOW360 && !user?.basic,
    DISABLED_POOLS: BROKERAGEASB || CLIENTFLOW360,
    CONTACT_PERMISSIONS: !user?.basic,
    MAX_AI_EMAIL: (ASB_SYSTEM && !BROKERAGEASB && !CLIENTFLOW360) || IS_DEV_STAGE_DEMO,
    SPOUSE:
      MYASBAGENT_SYSTEM ||
      MYASBFINANCIAL ||
      CFGSNE ||
      ASBLEGACY ||
      ASBAGENTMN ||
      HRGAGENT ||
      MYCSSAGENT ||
      CLIENTFLOW360,
    GENDER_IN_POLICY_SUBMISSION: ASB_SYSTEM,
    LANGUAGES_OFFICE_HOURS: AEGLEADS || (AGENTFLOW360 && ['BU5'].includes(user?.business_unit?.number)),
    SSO_FEATURE:
      (AEGLEADS ||
        ASBAGENTMN ||
        ASBCENTRAL ||
        IPAAGENT ||
        MYASBAGENT ||
        MYARCCRM ||
        MYAIMSAGENT ||
        LEGACYCRM ||
        HRGAGENT ||
        ASBLEGACY ||
        CFGSNE ||
        ASBMIDWEST ||
        MYASBFINANCIAL ||
        MYSIBAGENT ||
        IS_STAGE ||
        IS_DEMO) &&
      !IS_DEV,
    ACTIVITY_INFO: (ASB_SYSTEM && !BROKERAGEASB) || AEGLEADS,
    ACTIVITY_INFO_HIGHLIGHT: (ASB_SYSTEM && !BROKERAGEASB) || AEGLEADS,
    ACTIVITY_INFO_DROP_BY: ASBLEGACY || (MYASBAGENT && ['C150'].includes(user?.company?.number)),
    MARKETING_PROFILE: !user?.basic,
    RX_DETAILS: (ASB_SYSTEM && !BROKERAGEASB) || MYCSSAGENT || AEGLEADS || CLIENTFLOW360,
    LINKED_CONTACTS:
      ((MYMADCRM && ['C19', 'C20'].includes(user?.company?.number)) ||
        ASBLEGACY ||
        AEGLEADS ||
        MYASBAGENT ||
        ASBCENTRAL ||
        IPAAGENT ||
        MYSIBAGENT ||
        ASBMIDWEST ||
        MYCSSAGENT ||
        CFGSNE ||
        MYAIMSAGENT ||
        MYARCCRM ||
        LEGACYCRM ||
        CLIENTFLOW360 ||
        MYASBFINANCIAL) &&
      !user?.basic,
    DATA_CAPTURE_DEFAULT_FIELDS: HRGAGENT,
    DATA_CAPTURE_FIELDS: LEGACYCRM,
    CONTACTS_FILES_DROPZONE: ASBMIDWEST || AGENTFLOW360 || (MYASBAGENT && ['C136'].includes(user?.company?.number)),
    CONTACTS_FILES_MULTI_IMAGES_UPLOAD: ASB_SYSTEM,
    EMAIL_CAMPAIGNS: (ASB_SYSTEM && !BROKERAGEASB) || CLIENTFLOW360,
    EMAIL_GENERATION_APPROVAL: ['russellvancuren', 'jimhill', 'kevinsullivan', 'pavankotapally'].includes(
      user?.username
    ),
    SMS_CAMPAIGNS: (ASB_SYSTEM && !BROKERAGEASB) || CLIENTFLOW360,
    CONTACT_API_VENDOR: MYASBAGENT && (user?.admin || ['437'].includes(user?.number)), // 437 => John Tavare
    IS_TESTING_FEATURE: ['russellvancuren', 'jimhill', 'kevinsullivan', 'pavankotapally'].includes(user?.username),
    API_VENDORS: ASBLEGACY || MYASBAGENT || AEGLEADS,
    FLYER_UPLOAD: ASBLEGACY,
    MAILER_ASSIGNMENT: ASB_SYSTEM && !BROKERAGEASB && (user?.admin || user?.manager),
    SMS_FEATURE:
      (!user?.basic &&
        user?.wallet_feature_enabled &&
        user?.max_plus_subscription?.active &&
        user?.max_plus_subscription?.plan === 'premium') ||
      (CLIENTFLOW360 && user?.permissions?.[FEATURES.SMS_MARKETING]?.active === true),
    DIALER_FEATURE:
      !user?.basic &&
      ((MYMADCRM && ['C19', 'C20', 'C21'].includes(user?.company?.number)) ||
        (ASB_SYSTEM && !BROKERAGEASB) ||
        AGENTFLOW360),
    // REQUEST-79: ASB clause unchanged; CF360 clause is agency-credential driven.
    CSG_FEATURE: (ASB_SYSTEM && !BROKERAGEASB) || (CLIENTFLOW360 && user?.csg_enabled === true),
    MEDICARE_IMPORT_LEAD: ASB_SYSTEM,
    IMPORT_LEAD_TRACKING: user?.admin || user?.manager,
    CONTACTS_MERGE: (ASBLEGACY && user?.admin) || IS_DEMO,
    TRAINING_APP: IS_DEV_DEMO || (ASB_SYSTEM && !BROKERAGEASB) || MYCSSAGENT || AEGLEADS,
    API_TRACKING: IS_DEV_DEMO,
    TRAINING_REQUIRED: LEGACYCRM,
    QUICK_FILTERS: IS_DEV_DEMO || (ASB_SYSTEM && !BROKERAGEASB) || AEGLEADS || AGENTFLOW360 || CLIENTFLOW360,
    SOA_REQUESTS: !user?.basic && ((ASB_SYSTEM && !BROKERAGEASB) || MYCSSAGENT || AEGLEADS || CLIENTFLOW360),
    COMPANY_WEBPAGE: AEGLEADS || CLIENTFLOW360, // review functionality before adding more systems
    MEDICARE_POLICY_FIELDS: MYASBAGENT && (['C136'].includes(user?.company?.number) || ['761'].includes(user?.number)),
    MEDICARE_SSN_FIELDS: MYASBAGENT && ['C93', 'C151'].includes(user?.company?.number), // Nascimento
    DEMO_FEATURE: IS_DEV_DEMO,
    WALLET_FEATURE: user?.wallet_feature_enabled,
    MAD_LEADS: ASB_SYSTEM && user?.mad_leads_enabled,
    MAD_LEAD_TYPE_FIELD: ASB_SYSTEM && user?.admin && !user?.sub_admin,
    WORKFLOW_FEATURE:
      !user?.basic &&
      !CAREINADVANCE &&
      (IS_DEV_STAGE_DEMO ||
        user?.admin ||
        (user?.wallet_feature_enabled && user?.max_plus_subscription?.active) ||
        CLIENTFLOW360),
    DEMO_ASB_CENTRAL_FEATURE: ASBCENTRAL && IS_DEV_STAGE_DEMO,
    WF_IF_ELSE_CONDITION_FEATURE: ASBAGENTMN,
    AF360_BU5:
      // BU5 => ARIS
      (AGENTFLOW360 && ['BU5'].includes(user?.business_unit?.number)) ||
      (IS_DEV && ['BU12_test'].includes(user?.business_unit?.number)),
    AF360_BU9:
      // BU9 => American Legacy Solutions
      (AGENTFLOW360 && ['BU9'].includes(user?.business_unit?.number)) ||
      (IS_DEV && ['BU12_test'].includes(user?.business_unit?.number)),
    AF360_BU11: AF360_BU11, // BU11 => ASB Financial
    AF360_BU13: AF360_BU13, // BU13 => ASB Midwest
    AF360_BU11_WITH_SPECIFIC_USERS:
      // ID USERNAME
      // 71 johnoliver, 66 dustinsharples, 70 mendykornfeld, 46 tampapool, 63 amandadejiulio, 73 christianlaase,
      // 72 emilyjohnston, 74 travelldavis, 68 craigrupe, 64 scottparks, 75 joaomartins, 69 marcthompson, 65 barbarabaker,
      // 51 jacksonvillepool, 53 peachtreepool, 52 thevillagespool, 54 emeraldcoastpool, 48 tarponspringspool,
      // 50 bocaratonpool, 45 ocalapool, 55 lakelandpool, 49 lecantopool, 44 nancyleblanc, 102 darinbabb, 103 miamipool,
      // 148 mikerockhold, 149 toddriley
      AF360_BU11 &&
      [
        102, 103, 69, 49, 63, 65, 45, 44, 55, 71, 51, 66, 46, 64, 70, 50, 68, 48, 72, 52, 74, 75, 54, 73, 53, 148, 149
      ].includes(user?.id),
    MYASBAGENT_KULAKOVSKI_COMPANY: MYASBAGENT && ['C136'].includes(user?.company?.number), // C136 => Kulakovski company
    MYASBAGENT_BERG_MO_ST_LOUIS_COMPANY: MYASBAGENT && ['C148'].includes(user?.company?.number), // C148 => Berg, MO - St Louis company
    MYASBAGENT_STOCKMAN_OLATEH_KANSAS_COMPANY: MYASBAGENT && ['C139'].includes(user?.company?.number), // C139 => Stockman Olathe Kansas company
    CF360_POLICY_SOURCE_VISIBLE: CLIENTFLOW360 && ['C172'].includes(user?.company?.number), // C172 => GoldenCare company
    JIMHILL_ADMIN: user?.username === 'jimhill' && user?.admin,
    CONTACTS_SORT_BY_OWNER: MYASBAGENT && ['C14'].includes(user?.company?.number) && user?.manager, // C14 = Artman, PA - Pittsburgh
    CHAT_POPUP: (ASB_SYSTEM && !BROKERAGEASB && !MYASBAGENT) || AEGLEADS,
    /** AI chat (search, help, contact insight). Aegleads: on without marketing email generation. */
    CHAT_POPUP_CONTENT_CREATION: (ASB_SYSTEM && !BROKERAGEASB) || CLIENTFLOW360,
    /** MAX AI V2 — agentic chatbot with ReAct loop, streaming, and tool calls. */
    MAX_AI_V2: IS_DEV_STAGE_DEMO,
    SIDEKICK_FEATURE: IS_DEV_STAGE_DEMO || CLIENTFLOW360 || MYASBAGENT,
    WF_SHOW_CONTACT_GROUP: user?.manager || user?.bu_admin,
    GOOGLE_MAPS_API_KEY_FOR_DETAILS_PAGE: 'AIzaSyCNwYQmw-kbeotD-GYimnmchEAUpE6-ALs',
    GOOGLE_MAPS_API_KEY: 'AIzaSyDH3e7uIBZP6UjBFOLLU5G7eC-D9ZPpVx0',
    MAX_TRANSFER_LIMIT: 250_000,
    MAX_EXPORT_LIMIT: 999_999,
    MAX_PRINT_LIMIT: 500,
    MADREPORTS: ((ASB_SYSTEM && !BROKERAGEASB) || AEGLEADS) && user?.profile?.account_type !== 'agent',
    MADREPORTS_URL: IS_DEV ? 'http://localhost:8000' : 'https://madreports.com',
    FILTER_BOX_CONTACTS: false,
    FILTER_BOX_ADMIN_USERS: IS_DEV_STAGE_DEMO && [].includes(user?.username),
    CONTACTS_EXPANDED_ACTIONS_FEATURE: false,
    FAVORITE_LISTS: !CLIENTFLOW360 || user?.permissions?.[FEATURES.FAVORITE_LISTS]?.active === true,
    MULTIPLE_STATUSES: AGENTFLOW360 || CLIENTFLOW360,
    CONNECTED_CONTACTS: false, // TODO: Hidden for now. Will add connected contacts panel in Clientflow360 Phase 2
    SUGGESTED_MARKETING: CLIENTFLOW360,
    CF360_LEAD_FIELDS: CLIENTFLOW360 || IS_DEV_STAGE_DEMO,
    EXTENDED_PLAN: CLIENTFLOW360,
    CUSTOM_TABLE_V2: CLIENTFLOW360 || MYMADCRM,
    // BLOCK_AI_CALLS: IS_DEV,
    CLIENTFLOW360_PLAN: CLIENTFLOW360 && IS_DEV_DEMO,
    CF360_ONBOARDING: CLIENTFLOW360 || IS_DEV_STAGE_DEMO,
    CF360_SPOTLIGHT_TOUR: CLIENTFLOW360 || IS_DEV_STAGE_DEMO,
    CONTACT_INSIGHT: CLIENTFLOW360 || IS_DEV_STAGE_DEMO,
    CUSTOM_FIELDS: CLIENTFLOW360,
    AUTO_LEAD_ASSIGNMENT: (ASBLEGACY || CAREINADVANCE) && user?.admin,
    ADVANCED_EMAIL_PAGE: ASBLEGACY,
    DISABLED_SHARED_POLICY_TAB:
      ASBLEGACY && ['L1249', 'L3327', 'L3312', 'L3349', 'L3364', 'L3372'].includes(user?.number),
    DISABLED_SHARED_CALENDAR_TAB:
      ASBLEGACY && ['L1249', 'L3327', 'L3312', 'L3349', 'L3364', 'L3372'].includes(user?.number),
    // AGNT integration (REQUEST-70 POC) — local development only.
    AGNT_INTEGRATION: IS_DEV_STAGE_DEMO && CLIENTFLOW360,
    AF360_JOB_POSTINGS:
      AGENTFLOW360 &&
      ['BU5', 'BU8', 'BU9', 'BU10', 'BU11', 'BU13', 'BU16', 'BU17', 'BU20', 'BU23', 'BU25'].includes(
        user?.business_unit?.number
      ),
    SYSTEM_VERSION: {
      production_aegleads: '0.2.39',
      production_agentflow360: '0.2.40',
      default: '0.2.37'
    }
  };
};
